import { useCallback, useEffect, useState } from 'react';

/** 全画面の方式。`native` はブラウザの全画面 API、`immersive` は UI を隠す代替表示（FR-CAM-013）。 */
export type FullscreenMode = 'native' | 'immersive';

/** 全画面 API の一部。テストで差し替えられるよう、必要な部分だけを取り出している。 */
export interface FullscreenDocument {
  readonly fullscreenEnabled?: boolean;
  readonly fullscreenElement?: unknown;
  readonly documentElement: { readonly requestFullscreen?: () => Promise<void> };
  readonly exitFullscreen?: () => Promise<void>;
}

/** 全画面 API が使えれば `native`、使えない環境（iPhone の Safari など）では `immersive`。 */
export function fullscreenMode(doc: FullscreenDocument): FullscreenMode {
  return doc.fullscreenEnabled === true &&
    typeof doc.documentElement.requestFullscreen === 'function'
    ? 'native'
    : 'immersive';
}

/** 全画面表示の状態と切り替え。 */
export interface FullscreenControl {
  readonly mode: FullscreenMode;
  /** 全画面（または UI を隠した表示）になっているか */
  readonly active: boolean;
  readonly toggle: () => void;
}

/**
 * 全画面表示を切り替える。全画面 API が使えない環境では、UI を隠す表示で代替する。
 * 全画面 API が失敗（拒否）したときも、代替表示に切り替える。
 */
export function useFullscreen(): FullscreenControl {
  const [mode] = useState<FullscreenMode>(() => fullscreenMode(document));
  const [native, setNative] = useState(false);
  const [immersive, setImmersive] = useState(false);

  useEffect(() => {
    const onChange = (): void => {
      setNative(document.fullscreenElement !== null);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  const toggle = useCallback(() => {
    if (mode === 'native') {
      if (document.fullscreenElement !== null) {
        void document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen().catch(() => {
          // 拒否された場合は、UI を隠す表示で代替する
          setImmersive(true);
        });
      }
      return;
    }
    setImmersive((value) => !value);
  }, [mode]);

  // 代替表示のとき、Esc で戻れるようにする
  useEffect(() => {
    if (!immersive) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setImmersive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [immersive]);

  return { mode, active: native || immersive, toggle };
}
